// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React from 'react';
import { createPortal } from 'react-dom';
import './HelpModal.css';

const HelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="help-modal-overlay" onClick={onClose}>
      <div className="help-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="help-modal-header">
          <h2>🚀 STARSHIP CONSOLE - HELP</h2>
          <button className="help-close-btn" onClick={onClose}>✕</button>
        </div>
        
        <div className="help-modal-body">
          <section className="help-section">
            <h3>⌨️ Keyboard Shortcuts</h3>
            <div className="help-grid">
              <div className="help-item"><kbd>F1</kbd> Open Help (this screen)</div>
              <div className="help-item"><kbd>F2</kbd> View Reputation</div>
              <div className="help-item"><kbd>F3</kbd> Reset Game (with confirmation)</div>
              <div className="help-item"><kbd>ESC</kbd> Close Modals</div>
              <div className="help-item"><kbd>Space</kbd> Quick Actions</div>
            </div>
          </section>

          <section className="help-section">
            <h3>🎮 Core Gameplay</h3>
            <div className="help-content">
              <p><strong>Navigation:</strong> Click systems on the star map, set course, engage warp drive.</p>
              <p><strong>Fuel:</strong> Consumed during warp travel (0.33% per LY). Regenerates 2%/sec in orbit.</p>
              <p><strong>Combat:</strong> Target enemies, select weapons, choose fire mode (Single/Burst/Salvo).</p>
              <p><strong>Trading:</strong> Buy low, sell high. Different systems have different prices.</p>
            </div>
          </section>

          <section className="help-section">
            <h3>🗺️ Star Map</h3>
            <div className="help-content">
              <p><strong>Systems:</strong> Planets and stations with services (refuel, trade, repairs).</p>
              <p><strong>Warp Gates:</strong> Purple portals to other universes. No fuel cost to use.</p>
              <p><strong>Distance:</strong> Shown in light-years (LY). Plan routes carefully!</p>
              <p><strong>Colors:</strong> Green = visited, Yellow = selected, Cyan = current location.</p>
            </div>
          </section>

          <section className="help-section">
            <h3>⚔️ Combat System</h3>
            <div className="help-content">
              <p><strong>Fire Modes:</strong></p>
              <ul>
                <li>Single: 1.0x damage, one weapon</li>
                <li>Burst: 1.5x damage, 3 shots</li>
                <li>Salvo: 2.0x damage, all weapons</li>
              </ul>
              <p><strong>Bonuses:</strong> Critical hits (15% chance, 1.5x), Combo (+15% per hit), ACE (+20% at 3+ kills)</p>
            </div>
          </section>

          <section className="help-section">
            <h3>💰 Economy</h3>
            <div className="help-content">
              <p><strong>Trade Types:</strong></p>
              <ul>
                <li>Agricultural: Cheap food, expensive tech</li>
                <li>Industrial: Cheap tech, expensive food</li>
                <li>Mining: Cheap ore, expensive luxuries</li>
                <li>Luxury: Cheap art/wine, expensive basics</li>
              </ul>
              <p><strong>Tip:</strong> Buy from producers, sell to consumers!</p>
            </div>
          </section>

          <section className="help-section">
            <h3>🔧 Ship Management</h3>
            <div className="help-content">
              <p><strong>Upgrades:</strong> Improve engines, shields, cargo, fuel capacity.</p>
              <p><strong>Weapons:</strong> Purchase and reload at weapons shops.</p>
              <p><strong>Modules:</strong> Special equipment (scanners, cloaking, etc.).</p>
              <p><strong>Repairs:</strong> Hull damage repairs automatically in orbit (1%/sec).</p>
            </div>
          </section>

          <section className="help-section">
            <h3>👽 Alien Encounters</h3>
            <div className="help-content">
              <p><strong>Scanning:</strong> Use full scan to detect alien life on planets.</p>
              <p><strong>Dialogue:</strong> Some aliens are friendly, others hostile.</p>
              <p><strong>Trading:</strong> Tradeable aliens can be bought/sold (if you're into that).</p>
              <p><strong>Combat:</strong> Hostile aliens may attack during encounters.</p>
            </div>
          </section>

          <section className="help-section">
            <h3>💾 Save/Load</h3>
            <div className="help-content">
              <p><strong>Auto-Save:</strong> Game saves automatically every 60 seconds.</p>
              <p><strong>Manual Save:</strong> Use Save/Load button on console.</p>
              <p><strong>Multiple Slots:</strong> 5 save slots available.</p>
              <p><strong>Export/Import:</strong> Download saves as files for backup.</p>
            </div>
          </section>

          <section className="help-section">
            <h3>🎯 Tips & Tricks</h3>
            <div className="help-content">
              <ul>
                <li>Always keep fuel above 30% for emergencies</li>
                <li>Explore nearby systems first to build credits</li>
                <li>Upgrade fuel tank early for longer journeys</li>
                <li>Use warp gates to discover new universes (no fuel cost!)</li>
                <li>Scan planets for alien encounters and resources</li>
                <li>Combat gives full power restoration on victory</li>
                <li>Trade routes: Agricultural → Mining → Luxury → Industrial</li>
              </ul>
            </div>
          </section>

          <section className="help-section">
            <h3>🌌 Universe Warp Gate Network</h3>
            <div className="help-content">
              <p><strong>19 Interconnected Universes:</strong></p>
              <div style={{ textAlign: 'center', margin: '15px 0', background: '#0a0a1a', padding: '10px', borderRadius: '8px' }}>
                <img 
                  src="/help/universe-network.svg" 
                  alt="Universe Network Map" 
                  style={{ width: '100%', maxWidth: '900px', height: 'auto' }}
                />
              </div>
              <p><strong>Major Hubs (4+ connections):</strong></p>
              <ul>
                <li><strong>Milky Way</strong> - Central hub, connects to Pokémon, Mass Effect, DC, Discworld, Neon Metro</li>
                <li><strong>Mass Effect</strong> - Sci-fi hub, connects to Star Trek, Star Wars, Doctor Who, Halo, Warhammer</li>
                <li><strong>Neon Metropolis</strong> - Tech hub, connects to Improbability, Digimon, Marvel, DC</li>
                <li><strong>Discworld</strong> - Fantasy hub, connects to L-Space, Dungeon Dimensions, Improbability, Zelda</li>
              </ul>
              <p><strong>Quick Tips:</strong></p>
              <ul>
                <li>Warp gates appear as purple/magenta icons on star map</li>
                <li>Gates cost 50 energy to activate (no fuel cost)</li>
                <li>All gates are bidirectional (work both ways)</li>
                <li>Maximum 4 jumps to reach any universe from Milky Way</li>
              </ul>
            </div>
          </section>

          <section className="help-section">
            <h3>🌌 Universes</h3>
            <div className="help-content">
              <p>Explore 19 themed universes:</p>
              <ul>
                <li>Milky Way (starting universe)</li>
                <li>Star Wars Galaxy</li>
                <li>Doctor Who (Whoniverse)</li>
                <li>Pokémon Universe</li>
                <li>Sonic Universe</li>
                <li>Mario World</li>
                <li>Zelda (Hyrule)</li>
                <li>Digimon (Digital World)</li>
                <li>Neon Metropolis (Cyberpunk)</li>
                <li>Discworld</li>
                <li>Improbability Sector</li>
                <li>L-Space (Library dimension)</li>
                <li>Dungeon Dimensions</li>
              </ul>
            </div>
          </section>
        </div>

        <div className="help-modal-footer">
          <p>Press <kbd>F1</kbd> anytime to open this help screen</p>
          <button className="help-btn-primary" onClick={onClose}>Got It!</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default HelpModal;
